import type { LearnChain, LearnContext, LearnTopic } from './types';
import { whatIsAnOptionTopic } from './topics/what-is-an-option';
import { callsAndPutsTopic } from './topics/calls-and-puts';
import { buyingVsSellingTopic } from './topics/buying-vs-selling';
import { spreadsTopic } from './topics/spreads';
import { riskGraphTopic } from './topics/risk-graph';
import { condorTopic } from './topics/condor';
import { atRiskTopic } from './topics/at-risk';
import { capitalTopic } from './topics/capital';
import { rollTopic } from './topics/roll';
import { expirationTopic } from './topics/expiration';
import { deltaTopic } from './topics/delta';

export type { LearnChain, LearnContext, LearnLegInput, LearnTier, LearnTopic, LearnTopicId } from './types';
export { buildLearnContext, perSharePremium, finiteOrNull, shortLegDeltas } from './context';
export type { BuildLearnContextInput, DeltaLegInput } from './context';
export { payoffPerShare } from './format';

/**
 * Reading order: the five basics build from what an option is up to the iron condor, then the
 * position topics — the general shape first, then what is urgent, then the mechanics.
 */
export const LEARN_TOPICS: LearnTopic[] = [
  whatIsAnOptionTopic,
  callsAndPutsTopic,
  buyingVsSellingTopic,
  spreadsTopic,
  riskGraphTopic,
  condorTopic,
  atRiskTopic,
  capitalTopic,
  rollTopic,
  expirationTopic,
  deltaTopic,
];

/**
 * The topics an Explain panel shows for one position. Basics are excluded by TIER, not by
 * `appliesTo` — they apply to every position by design, so filtering on `appliesTo` alone would
 * put all five general write-ups in front of someone who asked about one trade.
 */
export function topicsFor(ctx: LearnContext, chain: LearnChain): LearnTopic[] {
  return LEARN_TOPICS.filter((topic) => topic.tier === 'position' && topic.appliesTo(ctx, chain));
}

/**
 * The fixed prompt behind "Ask AI to expand". Built from the same context the curated text uses,
 * so the AI paragraph and the sentences above it are describing the same numbers.
 */
export function buildLearnPrompt(ctx: LearnContext, chain: LearnChain): string {
  const f = (value: number | null, digits = 2) => (value == null ? 'unknown' : value.toFixed(digits));
  return [
    `Explain this ${ctx.symbol} position to someone learning iron condors.`,
    `Short put ${f(ctx.shortPut, 0)}, long put ${f(ctx.longPut, 0)}, short call ${f(ctx.shortCall, 0)}, long call ${f(ctx.longCall, 0)}.`,
    `Current price ${f(ctx.price)}. Credit collected ${f(ctx.credit)} per share. Capital held ${ctx.held == null ? 'unknown' : ctx.held.toLocaleString('en-US')}. Days to expiration ${ctx.dte ?? 'unknown'}.`,
    `Short deltas: put ${f(ctx.shortPutDelta)}, call ${f(ctx.shortCallDelta)}. The chain has been rolled ${chain.rollCount} time(s).`,
    'Cover what each spread is doing, which side is closer to risk and why, and what the held capital means. Use these figures. Keep it to three short paragraphs.',
  ].join(' ');
}
